import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, Plus, Trash2, X, Loader2, ImageIcon, RefreshCw, Copy, Globe } from 'lucide-react';
import { API } from '../App';
import ImageUploader from './ImageUploader';

const CATEGORIES = [
  'Fashion & Apparel',
  'Hats & Headwear',
  'Jewelry & Accessories',
  'Shoes & Footwear',
  'Bags & Accessories',
  'Beauty & Skincare',
  'Home & Decor',
  'Books & Media',
  'Agriculture & Farming',
  'Auto & Transportation',
  'Electronics & Tech',
  'Kids & Family',
  'Food & Groceries',
  'Other',
];

// Common subcategory hints per top category — user can override / free-type.
const SUBCATEGORY_HINTS = {
  'Fashion & Apparel': ['Modest Dresses', 'Sunday Best', 'Prayer Shawls', 'Shawls & Wraps', 'Worship Apparel'],
  'Hats & Headwear': ['Church Hats', 'Fascinators', 'Kufi Caps', 'Sun Hats'],
  'Jewelry & Accessories': ['Christian Jewelry', 'Bridal Jewelry', 'Ceremonial Jewelry', 'Watches', 'Rings'],
};

function newRowState() {
  return {
    id: Math.random().toString(36).slice(2, 9),
    name: '',
    description: '',
    category: 'Fashion & Apparel',
    subcategory: '',
    price: '',
    quantity: 100,
    imageUrl: '',
    images: [],
    // Digital book fields
    isDigital: false,
    digitalFileId: null,
    digitalFileName: null,
    digitalFileType: null,
    digitalUploading: false,
    saving: false,
    savedProductId: null,
  };
}

// ─── AliExpress one-click importer ────────────────────────────────
function AliExpressImporter({ onSaved }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('Fashion & Apparel');
  const [description, setDescription] = useState('');
  const [imagesText, setImagesText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!url.trim() || !name.trim() || !price) {
      toast.error('URL, name, and price are required.');
      return;
    }
    const images = imagesText
      .split(/\s+/)
      .map((u) => u.trim())
      .filter((u) => u.startsWith('http'));
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/products/aliexpress-import`, {
        url: url.trim(),
        name: name.trim(),
        price: Number(price),
        category,
        description: description.trim() || undefined,
        images: images.length > 0 ? images : undefined,
      });
      toast.success(`✓ Imported ${r.data.image_count} images — ${name}`);
      setUrl(''); setName(''); setPrice(''); setDescription(''); setImagesText('');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-[hsl(var(--gold))]/40" data-testid="aliexpress-importer">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-[hsl(var(--gold))]" /> Import from AliExpress
        </CardTitle>
        <CardDescription>
          Paste an AliExpress URL, right-click each product photo → &ldquo;Copy image address&rdquo;, and paste
          them below (one per line). The product goes live under <strong>Christ Kingdom Apparel</strong>
          tagged <code>source: aliexpress</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            data-testid="ali-url"
            placeholder="https://a.aliexpress.com/_xxxxx"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Input
            data-testid="ali-name"
            placeholder="Product name (e.g. Dashiki African Maxi Dress)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            data-testid="ali-price"
            type="number"
            step="0.01"
            placeholder="Price in USD (e.g. 39.99)"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="ali-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          data-testid="ali-description"
          rows={2}
          placeholder="Short description (optional — defaults to product name)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Product photos — upload directly OR paste image URLs</label>
          <ImageUploader
            testIdPrefix="ali-img"
            maxFiles={6}
            onUploaded={(url) => setImagesText((prev) => (prev ? prev + '\n' + url : url))}
          />
        </div>
        <Textarea
          data-testid="ali-images"
          rows={4}
          placeholder={'OR paste image URLs, one per line\nhttps://ae-pic-a1.aliexpress-media.com/kf/....jpg'}
          value={imagesText}
          onChange={(e) => setImagesText(e.target.value)}
        />
        <Button onClick={submit} disabled={saving} data-testid="ali-submit" className="bg-[hsl(var(--gold))] text-[hsl(var(--navy-dark))] hover:bg-[hsl(var(--gold))]/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          Import Product
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ProductBulkAdmin() {
  const [rows, setRows] = useState([newRowState()]);
  const [houseProducts, setHouseProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadHouseProducts = useCallback(async () => {
    try {
      setLoadingList(true);
      const r = await axios.get(`${API}/products`, { params: { limit: 100 } });
      const items = r.data?.products || r.data || [];
      setHouseProducts(items.filter((p) => p.vendor_name === 'Christ Kingdom Apparel'));
    } catch (_e) { /* silent */ } finally {
      setLoadingList(false);
    }
  }, []);
  useEffect(() => { loadHouseProducts(); }, [loadHouseProducts]);

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addImageToRow = (id) => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.id !== id) return r;
        const url = r.imageUrl.trim();
        if (!url) return r;
        if (!url.startsWith('http')) {
          toast.error('Image URL must start with http/https');
          return r;
        }
        return { ...r, images: [...r.images, url].slice(0, 6), imageUrl: '' };
      }),
    );
  };

  const removeImageFromRow = (id, imgIdx) =>
    updateRow(id, {
      images: rows.find((r) => r.id === id).images.filter((_v, i) => i !== imgIdx),
    });

  const addRow = () => setRows((rs) => [...rs, newRowState()]);
  const removeRow = (id) => setRows((rs) => (rs.length === 1 ? rs : rs.filter((r) => r.id !== id)));
  const duplicateRow = (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const copy = { ...row, id: Math.random().toString(36).slice(2, 9), savedProductId: null, saving: false };
    setRows((rs) => [...rs, copy]);
  };

  const saveRow = async (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (!row.name.trim() || !row.description.trim() || !row.price) {
      toast.error('Name, description, and price are required.');
      return;
    }
    if (row.isDigital && !row.digitalFileId) {
      toast.error('Digital book requires a PDF/EPUB/MOBI file. Please upload one.');
      return;
    }
    updateRow(id, { saving: true });
    try {
      const r = await axios.post(`${API}/products`, {
        name: row.name.trim(),
        description: row.description.trim(),
        price: Number(row.price),
        category: row.isDigital ? 'Books & Media' : row.category,
        quantity: Number(row.quantity) || 100,
        images: row.images.length > 0 ? row.images : undefined,
        is_digital: !!row.isDigital,
        digital_file_id: row.digitalFileId || undefined,
        digital_file_name: row.digitalFileName || undefined,
        digital_file_type: row.digitalFileType || undefined,
      });
      updateRow(id, { saving: false, savedProductId: r.data?.product_id });
      toast.success(`✓ ${row.name} — created`);
      loadHouseProducts();
    } catch (err) {
      updateRow(id, { saving: false });
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const uploadDigitalFile = async (id, file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too big — max 50 MB');
      return;
    }
    updateRow(id, { digitalUploading: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/products/upload-digital-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateRow(id, {
        digitalUploading: false,
        digitalFileId: r.data.file_id,
        digitalFileName: r.data.file_name,
        digitalFileType: r.data.file_type,
      });
      toast.success(`${r.data.file_name} uploaded (${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
    } catch (err) {
      updateRow(id, { digitalUploading: false });
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    }
  };

  const saveAll = async () => {
    const unsaved = rows.filter((r) => !r.savedProductId);
    if (unsaved.length === 0) {
      toast.info('Nothing to save — all rows already created.');
      return;
    }
    for (const r of unsaved) {
      if (r.name.trim() && r.description.trim() && r.price) {
        await saveRow(r.id);
      }
    }
    // Reset to a single fresh row after batch save
    setRows([newRowState()]);
  };

  const deleteHouseProduct = async (product_id, name) => {
    if (!window.confirm(`Delete "${name}"? This removes it from the storefront and Google Ads feed.`)) return;
    try {
      await axios.delete(`${API}/products/${product_id}`);
      toast.success('Deleted');
      loadHouseProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const copyImageUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Image URL copied');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="product-bulk-admin">
      <AliExpressImporter onSaved={loadHouseProducts} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" /> Bulk Product Upload
          </CardTitle>
          <CardDescription>
            Paste AliExpress / supplier photo URLs, add name + price, save. Each row becomes a live
            product under <strong>Christ Kingdom Apparel</strong>. Tip: right-click an AliExpress
            product image → &ldquo;Copy image address&rdquo; → paste here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6" data-testid="bulk-rows">
            {rows.map((row, idx) => {
              const subHints = SUBCATEGORY_HINTS[row.category] || [];
              return (
                <div
                  key={row.id}
                  className={`border rounded-xl p-4 ${row.savedProductId ? 'bg-green-50 border-green-300' : 'bg-background border-border'}`}
                  data-testid={`bulk-row-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium">
                      Product {idx + 1}
                      {row.savedProductId && (
                        <Badge className="ml-2 bg-green-600 hover:bg-green-700">Saved</Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => duplicateRow(row.id)} title="Duplicate row">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                        title="Remove row"
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <Input
                      value={row.name}
                      onChange={(e) => updateRow(row.id, { name: e.target.value })}
                      placeholder="Product name (e.g. Ivory Modest Sunday Dress)"
                      disabled={!!row.savedProductId}
                      data-testid={`bulk-name-${idx}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.price}
                      onChange={(e) => updateRow(row.id, { price: e.target.value })}
                      placeholder="Price USD"
                      disabled={!!row.savedProductId}
                      data-testid={`bulk-price-${idx}`}
                    />
                  </div>

                  {/* Digital book toggle */}
                  {!row.savedProductId && (
                    <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!row.isDigital}
                          onChange={(e) => updateRow(row.id, {
                            isDigital: e.target.checked,
                            category: e.target.checked ? 'Books & Media' : (row.category === 'Books & Media' ? 'Fashion & Apparel' : row.category),
                          })}
                          className="w-5 h-5 accent-purple-600"
                          data-testid={`bulk-is-digital-${idx}`}
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-purple-900">📚 This is a digital book (PDF / EPUB / MOBI)</div>
                          <div className="text-xs text-purple-700/80">Buyer gets an instant download link after payment. No shipping needed.</div>
                        </div>
                      </label>
                      {row.isDigital && (
                        <div className="mt-3 pl-8">
                          {row.digitalFileId ? (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs">✓</span>
                              <span className="font-medium text-green-800">{row.digitalFileName}</span>
                              <button
                                type="button"
                                onClick={() => updateRow(row.id, { digitalFileId: null, digitalFileName: null, digitalFileType: null })}
                                className="text-xs text-red-600 underline ml-2"
                                data-testid={`bulk-digital-remove-${idx}`}
                              >
                                Replace
                              </button>
                            </div>
                          ) : (
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="file"
                                accept=".pdf,.epub,.mobi,application/pdf,application/epub+zip"
                                className="hidden"
                                disabled={row.digitalUploading}
                                onChange={(e) => e.target.files?.[0] && uploadDigitalFile(row.id, e.target.files[0])}
                                data-testid={`bulk-digital-file-${idx}`}
                              />
                              <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border-2 border-dashed ${row.digitalUploading ? 'border-gray-300 bg-gray-100 text-gray-500' : 'border-purple-400 bg-white hover:bg-purple-100 text-purple-800 font-medium'}`}>
                                {row.digitalUploading ? (
                                  <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                                ) : (
                                  <>📄 Choose PDF / EPUB / MOBI (max 50 MB)</>
                                )}
                              </span>
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-3 mb-3">
                    <Select
                      value={row.category}
                      onValueChange={(v) => updateRow(row.id, { category: v })}
                      disabled={!!row.savedProductId || row.isDigital}
                    >
                      <SelectTrigger data-testid={`bulk-category-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={row.subcategory}
                      onChange={(e) => updateRow(row.id, { subcategory: e.target.value })}
                      placeholder={subHints[0] ? `e.g. ${subHints[0]}` : 'Subcategory (optional)'}
                      list={`subcat-${idx}`}
                      disabled={!!row.savedProductId}
                      data-testid={`bulk-subcategory-${idx}`}
                    />
                    {subHints.length > 0 && (
                      <datalist id={`subcat-${idx}`}>
                        {subHints.map((s) => <option key={s} value={s} />)}
                      </datalist>
                    )}
                    <Input
                      type="number"
                      min="0"
                      value={row.isDigital ? 999999 : row.quantity}
                      disabled={!!row.savedProductId || row.isDigital}
                      placeholder={row.isDigital ? 'Unlimited (digital)' : 'Stock qty'}
                      onChange={(e) => updateRow(row.id, { quantity: e.target.value })}
                      data-testid={`bulk-qty-${idx}`}
                    />
                  </div>

                  <Textarea
                    rows={2}
                    value={row.description}
                    onChange={(e) => updateRow(row.id, { description: e.target.value })}
                    placeholder="Short description (mention material, fit, occasion, care instructions)"
                    className="mb-3"
                    disabled={!!row.savedProductId}
                    data-testid={`bulk-description-${idx}`}
                  />

                  {/* Images */}
                  <div className="mb-1">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Input
                        value={row.imageUrl}
                        onChange={(e) => updateRow(row.id, { imageUrl: e.target.value })}
                        placeholder="Paste image URL OR use the Upload button →"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImageToRow(row.id))}
                        disabled={!!row.savedProductId}
                        data-testid={`bulk-image-url-${idx}`}
                        className="flex-1 min-w-[200px]"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => addImageToRow(row.id)}
                        disabled={!!row.savedProductId}
                        data-testid={`bulk-image-add-${idx}`}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add URL
                      </Button>
                      {!row.savedProductId && (
                        <ImageUploader
                          compact
                          testIdPrefix={`bulk-img-${idx}`}
                          maxFiles={6}
                          onUploaded={(url) => setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, images: [...r.images, url].slice(0, 6) } : r))}
                        />
                      )}
                    </div>
                    {row.images.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {row.images.map((url, i) => (
                          <div key={url + i} className="relative w-20 h-20 rounded-md overflow-hidden border border-border group">
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            {!row.savedProductId && (
                              <button
                                type="button"
                                onClick={() => removeImageFromRow(row.id, i)}
                                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {row.images.length === 0 && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <ImageIcon className="h-3 w-3" /> No images yet — a placeholder will be used if you save without one.
                      </div>
                    )}
                  </div>

                  {!row.savedProductId && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={() => saveRow(row.id)}
                        disabled={row.saving}
                        size="lg"
                        className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                        data-testid={`bulk-save-${idx}`}
                      >
                        {row.saving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Plus className="h-5 w-5 mr-2" />}
                        Publish product to storefront
                      </Button>
                    </div>
                  )}
                  {row.savedProductId && (
                    <div className="mt-4 flex items-center justify-end gap-2 text-sm font-semibold text-green-700" data-testid={`bulk-saved-${idx}`}>
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white">✓</span>
                      Published — live on storefront now
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
            <Button variant="outline" onClick={addRow} data-testid="bulk-add-row">
              <Plus className="h-4 w-4 mr-1" /> Add another product
            </Button>
            <Button onClick={saveAll} size="lg" className="bg-primary hover:bg-primary/90 font-semibold" data-testid="bulk-save-all">
              <Plus className="h-4 w-4 mr-2" /> Publish all products
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live house-brand catalog */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Live Christ Kingdom Apparel catalog</CardTitle>
            <CardDescription>All house-brand products currently visible to shoppers &amp; Google Ads.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={loadHouseProducts} disabled={loadingList}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingList ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : houseProducts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4" data-testid="house-catalog-empty">
              No house-brand products yet. Add your first above.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="house-catalog-grid">
              {houseProducts.map((p) => (
                <div
                  key={p.product_id}
                  className="border border-border rounded-lg overflow-hidden bg-background flex flex-col"
                  data-testid={`house-product-${p.product_id}`}
                >
                  <div className="aspect-square bg-muted overflow-hidden relative group">
                    <img
                      src={(p.images && p.images[0]) || 'https://images.unsplash.com/photo-1655147999706-91d0f00e7754?w=400'}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition flex items-center gap-1"
                      onClick={() => copyImageUrl(p.images?.[0] || '')}
                      title="Copy image URL"
                    >
                      <Copy className="h-3 w-3" /> URL
                    </button>
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {p.subcategory || p.category}
                    </div>
                    <h4 className="font-medium mt-1 line-clamp-2 flex-1">{p.name}</h4>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-primary font-medium">${Number(p.price).toFixed(2)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteHouseProduct(p.product_id, p.name)}
                        className="text-destructive hover:bg-destructive/10 px-2"
                        data-testid={`house-product-delete-${p.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
